const sliderCache = new WeakMap();
export const projectionChartSliderPlugin = {
    id: 'projectionChart',
    install(chart, args, options) {
        const { onInstall } = options;
        onInstall(chart);
        const setDefaultSliders = () => {
            sliderCache.set(chart, {
                left: null,
                right: null,
                storedLeft: null,
                storedRight: null,
                forMin: null,
                forMax: null,
                lastElement: null,
                lastEvent: null,
            });
        };
        setDefaultSliders();
    },
    afterDataLimits(chart, { scale }, options) {
        if (scale.id !== 'x') {
            return;
        }
        const sliders = sliderCache.get(chart);
        let slidersChanged = false;
        if (sliders.left === null) {
            sliders.forMin = scale.min;
        }
        else {
            const isMinChanged = sliders.forMin !== scale.min;
            if (isMinChanged) {
                sliders.forMin = scale.min;
                if (sliders.left < scale.min) {
                    sliders.storedLeft = sliders.left;
                    sliders.left = scale.min;
                    slidersChanged = true;
                }
                else if (sliders.storedLeft !== null && sliders.storedLeft < sliders.left && sliders.left > scale.min) {
                    sliders.left = sliders.storedLeft;
                    sliders.storedLeft = null;
                    slidersChanged = true;
                }
            }
        }
        if (sliders.right === null) {
            sliders.forMax = scale.max;
        }
        else {
            const isMaxChanged = sliders.forMax !== scale.max;
            if (isMaxChanged) {
                sliders.forMax = scale.max;
                if (sliders.right > scale.max) {
                    sliders.storedRight = sliders.right;
                    sliders.right = scale.max;
                    slidersChanged = true;
                }
                else if (sliders.storedRight !== null && sliders.storedRight > sliders.right && sliders.right < scale.max) {
                    sliders.right = sliders.storedRight;
                    sliders.storedRight = null;
                    slidersChanged = true;
                }
            }
        }
        if (slidersChanged && options.onSliderChange) {
            options.onSliderChange(sliders);
        }

        // TODO: ITS FIX IN COMPILED CODE
        scale.options.min = scale.min;
        scale.options.max = scale.max;
    },
    beforeEvent(chart, args, options) {
        handleHover(chart, args.event);
        if (handleDragEvent(chart, args.event, options)) {
            // @ts-ignore
            args.changed = true;
        }
    },
    afterDraw(chart) {
        const sliders = sliderCache.get(chart);
        const xScale = chart.scales.x;
        const { chartArea } = chart;
        const leftValue = sliders.left === null ? sliders.forMin : sliders.left;
        const rightValue = sliders.right === null ? sliders.forMax : sliders.right;
        if (!chartArea) {
            return;
        }
        if (leftValue === null || rightValue === null) {
            return;
        }
        let left = xScale.getPixelForValue(leftValue);
        let right = xScale.getPixelForValue(rightValue);
        const scaleFromPixel = xScale.getPixelForValue(xScale.min);
        const scaleToPixel = xScale.getPixelForValue(xScale.max);
        const yCenter = chartArea.top + chartArea.height / 2;
        const isLeftOnScaleMin = Math.abs(left - scaleFromPixel) < 10;
        const isRightOnScaleMax = Math.abs(scaleToPixel - right) < 10;
        const leftBorder = isLeftOnScaleMin ? left - scaleFromPixel : 10;
        const rightBorder = isRightOnScaleMax ? scaleToPixel - right : 10;
        const basicOffset = 1;
        if (isLeftOnScaleMin) {
            left += basicOffset;
        }
        if (isRightOnScaleMax) {
            right -= basicOffset;
        }
        drawRects(chart, left, right, leftBorder, rightBorder);
        drawSliderDragButton(chart, left, yCenter);
        drawSliderDragButton(chart, right, yCenter);
    },
};
function handleHover(chart, event) {
    const sliders = sliderCache.get(chart);
    const leftValue = sliders.left === null ? sliders.forMin : sliders.left;
    const rightValue = sliders.right === null ? sliders.forMax : sliders.right;
    if (event.type === 'mousemove' && !sliders.lastEvent) {
        const mouseX = event.x;
        const { scales } = chart;
        if (leftValue === null || rightValue === null) {
            return;
        }
        const leftPixels = scales.x.getPixelForValue(leftValue);
        const rightPixels = scales.x.getPixelForValue(rightValue);
        const minLeftPixels = leftPixels - 10;
        let maxLeftPixels = leftPixels + 10;
        let minRightPixels = rightPixels - 10;
        const maxRightPixels = rightPixels + 10;
        if (minRightPixels < maxLeftPixels) {
            const diff = (rightPixels - leftPixels) / 2;
            maxLeftPixels = leftPixels + diff;
            minRightPixels = rightPixels - diff;
        }
        if (mouseX >= minLeftPixels && mouseX <= maxLeftPixels) {
            sliders.lastElement = 'left';
            chart.canvas.style.cursor = 'e-resize';
        }
        else if (mouseX >= minRightPixels && mouseX <= maxRightPixels) {
            sliders.lastElement = 'right';
            chart.canvas.style.cursor = 'e-resize';
        }
        else {
            sliders.lastElement = null;
            chart.canvas.style.cursor = 'default';
        }
    }
}
function handleDragEvent(chart, event, options) {
    const sliders = sliderCache.get(chart);
    if (sliders.lastElement) {
        switch (event.type) {
            case 'mousemove':
                return sliderDragging(chart, event);
            case 'mouseout':
            case 'mouseup':
                sliders.lastEvent = null;
                if (sliders.storedLeft !== null) {
                    sliders.storedLeft = sliders.left;
                }
                if (sliders.storedRight !== null) {
                    sliders.storedRight = sliders.right;
                }
                if (options.onSliderChange) {
                    options.onSliderChange(sliders);
                }
                break;
            case 'mousedown':
                sliders.lastEvent = event;
                break;
            default:
        }
    }
}
function sliderDragging(chart, event) {
    const sliders = sliderCache.get(chart);
    const { scales } = chart;
    const leftValue = sliders.left === null ? sliders.forMin : sliders.left;
    const rightValue = sliders.right === null ? sliders.forMax : sliders.right;
    if (!sliders.lastEvent || !sliders.lastElement) {
        return false;
    }
    if (leftValue === null || rightValue === null) {
        return false;
    }
    const newX = scales.x.getValueForPixel(event.x);
    if (newX === undefined || newX === null) {
        return false;
    }
    if (sliders.lastElement === 'left' && newX > rightValue) {
        return false;
    }
    if (sliders.lastElement === 'right' && newX < leftValue) {
        return false;
    }
    if (newX < sliders.forMin) {
        sliders[sliders.lastElement] = null;
        return true;
    }
    if (newX > sliders.forMax) {
        sliders[sliders.lastElement] = null;
        return true;
    }
    sliders[sliders.lastElement] = newX;
    return true;
}
function drawRects(chart, left, right, leftVerticalLine, rightVerticalLine) {
    const { chartArea, ctx } = chart;
    const leftRoundRect = [0, leftVerticalLine, 0, 0];
    const rightRoundRect = [rightVerticalLine, 0, 0, 0];
    ctx.save();
    ctx.fillStyle = 'rgba(41,200,204,0.46)';
    ctx.strokeStyle = 'rgba(41,200,204,0.46)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(left, chartArea.top);
    drawRoundRect(ctx, chartArea.left, chartArea.top, left - chartArea.left, chartArea.height, leftRoundRect);
    ctx.moveTo(right, chartArea.top);
    drawRoundRect(ctx, right, chartArea.top, chartArea.right - right, chartArea.height, rightRoundRect);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
}
function drawSliderDragButton(chart, x, y) {
    const { ctx } = chart;
    ctx.save();
    ctx.beginPath();
    // ellipse
    ctx.ellipse(x, y, 9, 9, 0, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgb(220,243,242)';
    ctx.fill();
    ctx.moveTo(x - 3, y - 5);
    ctx.lineTo(x - 3, y + 5);
    ctx.moveTo(x, y - 5);
    ctx.lineTo(x, y + 5);
    ctx.moveTo(x + 3, y - 5);
    ctx.lineTo(x + 3, y + 5);
    ctx.strokeStyle = '#3b4141';
    ctx.stroke();
    ctx.restore();
}
function drawRoundRect(ctx, x, y, width, height, radii) {
    const [topLeft, topRight, bottomRight, bottomLeft] = radii;
    ctx.moveTo(x + topLeft, y);
    ctx.lineTo(x + width - topRight, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + topRight);
    ctx.lineTo(x + width, y + height - bottomRight);
    ctx.quadraticCurveTo(x + width, y + height, x + width - bottomRight, y + height);
    ctx.lineTo(x + bottomLeft, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - bottomLeft);
    ctx.lineTo(x, y + topLeft);
    ctx.quadraticCurveTo(x, y, x + topLeft, y);
}
//# sourceMappingURL=projectionChartSliderPlugin.js.map
